<template>
    <div class="tile-content transaction-summary-actions">
        <div class="grid-fitted align-items-center">
            <div class="grid-item inline-s">
                <transaction-summary-retry-message v-if="transaction.isRetry && transaction.isScheduled" :retry="transaction">
                            <span slot-scope="retry">
                                Scheduled retry {{retry.attempts}} from initial transaction
                            </span>
                </transaction-summary-retry-message>
                <span v-else>{{ getStateMessage(transaction) }}</span>
            </div>
            <div class="grid-item grid-fitted align-items-center no-flex transaction-summary-actions-action-list">
                <div v-if="canDispute" class="grid-item inline-s">
                    <router-link
                        :disabled="isProcessing"
                        class="btn" :to="{name: 'add-dispute', params:{transactionId: transaction.id}}">
                        Dispute
                    </router-link>
                </div>
                <div v-if="canCancel" class="grid-item inline-s no-flex">
                    <r-button :disabled="isProcessing" @click="showCancelDialog">Cancel</r-button>
                </div>
                <div v-if="canVoid" class="grid-item inline-s no-flex">
                    <r-button :disabled="isProcessing" @click="showVoidDialog">Void</r-button>
                </div>
                <amount-form-modal v-if="canCapture" title="capture" class="grid-item inline-s no-flex"
                                   :max-amount="transaction.amount"
                                   :currency-code="transaction.currency"
                                   @submit="captureTransaction">
                    <p slot="message">
                        You can modify the amount to apply a partial capture or leave as is for a full capture.
                    </p>
                </amount-form-modal>
                <amount-form-modal v-if="canRefund"
                                   :is-processing="isProcessing"
                                   :title="refundTitle"
                                   class="grid-item inline-s no-flex"
                                   :max-amount="transaction.refundableAmount"
                                   :currency-code="transaction.currency"
                                   @submit="refundTransaction">
                    <div slot="message">
                        <div  v-if="transaction.hasRefunds" class="message message-warning stack-m">
                            <div class="message-content">
                                <p>This transaction was partially refunded before. The remaining balance stands at {{transaction.refundableAmount | monetizeExplicit(transaction.currency)}} out of the original {{transaction.amount | monetizeExplicit(transaction.currency)}}</p>
                            </div>
                        </div>
                        <p>You can modify the amount to apply a partial refund or leave as is for a full refund.</p>
                    </div>
                </amount-form-modal>
                <!--TODO previously rescheduling of the transaction was here-->
            </div>
        </div>
    </div>
</template>
<script>
    import {vIcon} from '@/components/form';
    import {createDialog, vConfirm} from '@/components/modal/dialogs';
    import _ from '@/common/helpers';
    import AmountFormModal from './amount-form-modal';
    import DatetimeFormModal from './datetime-form-modal';
    import TransactionSummaryRetryMessage from './transaction-summary-retry-message';
    import TransactionModel from '@/common/models/transaction-model';

    export default {
        name: 'transaction-summary-actions',
        components: {
            TransactionSummaryRetryMessage,
            AmountFormModal,
            DatetimeFormModal,
            vIcon,
        },
        props: {
            transaction: {
                type: Object,
            },
            isProcessing: {
                type: Boolean,
                required: false,
                default: false,
            },
        },
        computed: {
            canCancel() {
                return this.$auth.transactions.cancel.isAllowed && this.transaction.canCancel;
            },
            canVoid() {
                return this.$auth.transactions.void.isAllowed && this.transaction.canVoid;
            },
            canCapture() {
                return this.$auth.transactions.capture.isAllowed && this.transaction.canCapture;
            },
            canRefund() {
                return this.$auth.transactions.refund.isAllowed && this.transaction.canRefund;
            },
            canReschedule() {
                return this.$auth.transactions.reschedule.isAllowed && this.transaction.isScheduled;
            },
            canDispute() {
                return this.$auth.transactions.dispute.isAllowed && this.transaction.canDispute;
            },
            refundTitle() {
                if (!this.transaction.refundableAmount) {
                    return 'refunded';
                } if (this.transaction.hasRefunds) {
                    return 'refund (partial)';
                }
                return 'refund';
            },
        },
        methods: {
            gatewayResponse(transaction) {
                if (transaction.gateway.response.message === null
                    && transaction.gateway.response.originalMessage === null) {
                    return 'Unknown Reason';
                }
                return transaction.gateway.response.message !== null
                    ? transaction.gateway.response.message
                    : transaction.gateway.response.originalMessage;
            },
            // Confirmation Dialogs
            async showCancelDialog() {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: 'Cancel Transaction',
                    content: 'Are you sure you want to cancel this transaction?',
                    confirmLabel: 'Yes',
                    cancelLabel: 'No',
                };
                if (await confirmDialog(dialogParams)) {
                    this.cancelTransaction();
                }
            },
            async showVoidDialog() {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: 'Void Transaction',
                    content: 'Are you sure you want to void this transaction?',
                    confirmLabel: 'Yes',
                    cancelLabel: 'No',
                };
                if (await confirmDialog(dialogParams)) {
                    this.refundTransaction(this.transaction.amount, 'void');
                }
            },
            // Transaction Actions
            async cancelTransaction() {
                let error = null;
                this.$emit('action-starts');
                try {
                    const param = {id: this.transaction.id};
                    await this.$api(({transactions}) => transactions.cancel(param));
                } catch (apiError) {
                    error = apiError;
                } finally {
                    this.$emit('action-ends', this.createActionResult('cancel', error));
                }
            },
            async refundTransaction(amountToRefund, actionName = 'refund') {
                let error = null;
                let refundedTransaction;
                this.$emit('action-starts');
                const params = {
                    id: this.transaction.id,
                    data: {
                        amount: amountToRefund,
                    },
                };
                try {
                    const {fields} = await this.$api(({transactions}) => transactions.refund(params));
                    refundedTransaction = new TransactionModel(fields);
                } catch (apiError) {
                    error = apiError;
                } finally {
                    this.$emit('action-ends', this.createActionResult(actionName, error, refundedTransaction));
                }
            },
            async captureTransaction(captureAmount) {
                let error = null;
                this.$emit('action-starts');
                const params = {
                    data: this.transaction.getCapturePayload({amount: captureAmount}),
                };
                try {
                    await this.$api(({transactions}) => transactions.create(params));
                } catch (apiError) {
                    error = apiError;
                } finally {
                    this.$emit('action-ends', this.createActionResult('capture', error));
                }
            },
            // Action result message helper
            createActionResult(actionName, error, refundedTransaction = null) {
                const result = {};
                if (error) {
                    result.type = 'negative';
                    result.message = `Unable to ${actionName} this transaction at this time.`;
                    if (error.details) {
                        result.message += `\n Details: ${error.details.join(' ')}`;
                    }
                } else if (refundedTransaction !== null
                    && (refundedTransaction.isDeclined)) {
                    result.type = 'negative';
                    result.message = `Transaction for ${this.$filters.monetizeExplicit(refundedTransaction.amount, refundedTransaction.currency)} was declined (${this.gatewayResponse(refundedTransaction)}) by ${refundedTransaction.gatewayName}.`;
                } else {
                    result.type = 'positive';
                    result.message = `${_.capitalize(actionName)} was successfully applied
                        to transaction ${this.transaction.id}`;
                }
                return result;
            },
            getStateMessage(transaction) {
                const builders = {
                    'approved/refund': ({parentTransactionId, gatewayName, gatewayAccountId}) => `Transaction ${parentTransactionId} was refunded by ${gatewayName} (${gatewayAccountId})`,
                    'approved/void': ({parentTransactionId, gatewayName, gatewayAccountId}) => `Transaction ${parentTransactionId} was voided by ${gatewayName} (${gatewayAccountId})`,
                    approved: ({gatewayName, gatewayAccountId}) => `Transaction was approved by ${gatewayName} (${gatewayAccountId})`,
                    declined: ({gatewayName, gatewayAccountId}) => `Transaction was declined "${this.gatewayResponse(transaction)}" by ${gatewayName} (${gatewayAccountId})`,
                    canceled: () => 'Transaction was canceled',
                    unknown: () => 'Transaction result is unknown',
                };
                const {result, type} = transaction;
                const messageBuilder = builders[`${result}/${type}`]
                    || builders[result]
                    || builders.unknown;
                return messageBuilder(transaction);
            },
        },
    };
</script>
